import {Component, computed, effect, inject, signal} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {toSignal} from '@angular/core/rxjs-interop';
import {filter, lastValueFrom, map} from 'rxjs';
import {FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {TranslatePipe} from '@ngx-translate/core';
import {
  Address,
  CompanyDetails,
  CompanyService,
  CreateCompanyRequest,
  DirectDebitMandate,
  PaymentMethod,
  UpdateCompanyRequest
} from '../../../../../api';
import {QueryKey} from '../../../../constants/query-key';
import {Title} from '../../../../components/title/title';
import {TabItem, TabMenu} from '../../../../components/tab-menu/tab-menu';
import {AddressFormModel} from '../address-form/address-form.model';
import {DirectDebitFormModel, DirectDebitFormValue} from '../direct-debit-form/direct-debit-form.model';
import {CompanyFormModel, CreateCompanyFormValue} from '../company-form/company-form.model';
import {DirectoryStore} from '../../services/directory-store';
import {CompanyForm} from '../company-form/company-form';
import {RouteUri} from '../../../../constants/route-uri';
import {ElciaCard} from '../../../../components/card/card';
import {CurrentUserService} from '../../../../services/current-user.service';
import {SaveBar} from '../../../../components/save-bar/save-bar';
import {HasUnsavedChanges} from '../../../../guards/unsaved-changes.guard';

@Component({
  selector: 'elcia-company-form-home',
  imports: [
    Title,
    TranslatePipe,
    TabMenu,
    ElciaCard,
    RouterOutlet,
    CompanyForm,
    SaveBar
  ],
  templateUrl: './company-form-home.html',
  styleUrl: './company-form-home.scss',
})

export class CompanyFormHome implements HasUnsavedChanges {

  private readonly route = inject(ActivatedRoute);
  private readonly companyService = inject(CompanyService);
  private readonly directoryStore = inject(DirectoryStore);
  private readonly router = inject(Router);
  private readonly currentUserService = inject(CurrentUserService);

  protected readonly customerId = toSignal(this.route.params.pipe(map(params => params['id'] ? Number(params['id']) : null)));

  protected readonly isEditMode = computed(() => this.customerId() != null);

  private readonly companyQuery = injectQuery(() => ({
    queryKey: [QueryKey.COMPANY, this.customerId()],
    queryFn: () => lastValueFrom(this.companyService.getCompanyById(this.customerId()!)),
    enabled: this.customerId() != null,
  }));

  protected readonly customerName = computed(() => this.companyQuery.data()?.company.name ?? '');

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    {initialValue: this.router.url},
  );

  protected readonly isChildTabActive = computed(() =>
    this.isEditMode() && (
      this.currentUrl().includes(RouteUri.DIRECTORY_FORM_PRODUCTS) ||
      this.currentUrl().includes(RouteUri.DIRECTORY_FORM_BILLING) ||
      this.currentUrl().includes(RouteUri.DIRECTORY_FORM_LICENCE_SERVERS)
    ));

  protected readonly items = computed<TabItem[]>(() => {
    const items: TabItem[] = [
      {
        icon: 'file',
        label: 'app.informations',
        route: RouteUri.DIRECTORY_FORM_DETAILS,
        hasPendingChanges: this.isDirty(),
      },
    ];

    // The products tab requires the CanManageCustomerProducts permission.
    if (this.currentUserService.hasManageCustomerProducts()) {
      items.push({
        icon: 'tag',
        label: 'entity.products',
        route: RouteUri.DIRECTORY_FORM_PRODUCTS,
      });
    }

    // The licence server tab requires the CanManageLicenceServer permission.
    if (this.currentUserService.hasLicenceServerAccess()) {
      items.push({
        icon: 'server',
        label: 'licence-server.title',
        route: RouteUri.DIRECTORY_FORM_LICENCE_SERVERS,
      });
    }

    // The billing tab requires finance access.
    if (this.currentUserService.hasFinanceAccess()) {
      items.push({
        icon: 'finances',
        label: 'customer-billing.title',
        route: RouteUri.DIRECTORY_FORM_BILLING,
      });
    }

    return items;
  });

  protected readonly form = this.createCompanyForm();

  // form.dirty / form.invalid are not signals: form.events republishes every pristine and status change
  private readonly formEvent = toSignal(this.form.events);
  protected readonly isDirty = computed(() => {
    this.formEvent();
    return this.form.dirty;
  });
  protected readonly isInvalid = computed(() => {
    this.formEvent();
    return this.form.invalid;
  });

  protected readonly isSaving = computed(() =>
    this.directoryStore.createCompanyMutation.isPending() || this.directoryStore.updateCompanyMutation.isPending());

  protected readonly savedAt = signal<Date | null>(null);

  // Selected payment method, surfaced by the company form; the direct-debit mandate is only sent for DirectDebit
  protected readonly selectedPaymentMethod = signal<PaymentMethod | null>(null);
  private readonly isDirectDebit = computed(() => this.selectedPaymentMethod() === PaymentMethod.DirectDebit);

  // En domiciliation, iban/bic/reference/signedAt deviennent obligatoires ; sinon on relâche la contrainte
  private readonly toggleDirectDebitValidators = effect(() => {
    const required = this.isDirectDebit();
    const controls = this.form.controls.directDebitForm.controls;
    for (const control of [controls.iban, controls.bic, controls.reference, controls.signedAt]) {
      control.setValidators(required ? Validators.required : null);
      control.updateValueAndValidity({emitEvent: false});
    }

    // En domiciliation, préremplit la date de signature avec aujourd'hui si elle est vide
    if (required && controls.signedAt.value == null) {
      controls.signedAt.setValue(new Date());
    }
  });

  // The Belgian VAT check depends on the address country, so revalidate the VAT whenever it changes
  private readonly addressCountryIsoCode = toSignal(
    this.form.controls.addressForm.controls.countryIsoCode.valueChanges,
    {initialValue: this.form.controls.addressForm.controls.countryIsoCode.value},
  );
  private readonly revalidateVatOnCountryChange = effect(() => {
    this.addressCountryIsoCode();
    this.form.controls.vatNumber.updateValueAndValidity({emitEvent: false});
  });

  // Peuple le form une seule fois par company chargée : un refetch (déclenché par
  // l'invalidate après sauvegarde) ne doit pas réécraser les éditions en cours via patchValue
  private populatedCompanyId: number | null = null;
  private readonly populateForm = effect(() => {
    const company = this.companyQuery.data()?.company;
    if (!company) return;
    if (company.id === this.populatedCompanyId) return;
    this.populatedCompanyId = company.id;
    this.patchFromCompany(company);
  });

  // En création, présélectionne succursale, langue et pays du user connecté
  private readonly presetCreateDefaults = effect(() => {
    if (this.isEditMode()) return;
    this.applyCreateDefaults();
  });

  private patchFromCompany(company: CompanyDetails) {
    this.form.patchValue({
      name: company.name,
      guid: company.guid ?? null,
      website: company.webSite ?? '',
      email: company.email ?? '',
      accountingAccountName: company.accountingAccountName,
      vatNumber: company.taxIdentifierNumber ?? '',
      note: company.note ?? '',
      contactIds: company.contacts.map(contact => contact.id),
      legalEntityAssignedId: company.legalEntityAssignedId ?? null,
      localeIsoCode: company.localeIsoCode ?? this.currentUserService.currentUser()?.localeIsoCode ?? null,
      isSameAddressForBilling: company.isSameAddressForBillingAddress,
      isActive: company.isActive,
      addressForm: this.mapAddress(company.address),
      billingAddressForm: this.mapAddress(company.billingAddress),
      paymentModeId: company.paymentModeId ?? null,
      directDebitForm: this.mapDirectDebit(company.directDebit),
    });
  }

  // Ne remplace jamais une saisie manuelle. Rejoué par « Ignorer », qui repart d'un form vierge.
  private applyCreateDefaults() {
    const user = this.currentUserService.currentUser();
    const controls = this.form.controls;

    if (user?.legalEntityId != null && controls.legalEntityAssignedId.value == null) {
      controls.legalEntityAssignedId.setValue(user.legalEntityId);
    }

    if (user?.localeIsoCode != null && controls.localeIsoCode.value == null) {
      controls.localeIsoCode.setValue(user.localeIsoCode);
    }

    const countryIsoCode = this.currentUserService.defaultCountryIsoCode();
    [controls.addressForm.controls.countryIsoCode, controls.billingAddressForm.controls.countryIsoCode]
      .filter(control => !control.dirty)
      .forEach(control => control.setValue(countryIsoCode));
  }

  private mapAddress(address?: Address) {
    return {
      street: address?.street ?? null,
      number: address?.number ?? null,
      postalCode: address?.postalCode ?? null,
      city: address?.city ?? null,
      countryIsoCode: address?.countryIsoCode ?? 'BE',
    };
  }

  private mapDirectDebit(mandate?: DirectDebitMandate): DirectDebitFormValue {
    return {
      iban: mandate?.iban ?? null,
      bic: mandate?.bic ?? null,
      reference: mandate?.reference ?? null,
      signedAt: mandate?.signedAt ? new Date(mandate.signedAt) : null,
    };
  }

  hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  // Édition : reste sur la fiche. Création : redirige vers la grille.
  protected submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const company = this.companyQuery.data()?.company;

    if (company) {
      this.directoryStore.updateCompanyMutation.mutate({
        id: company.id,
        body: this.mapToUpdateRequest(value, company),
      }, {
        onSuccess: () => {
          this.form.markAsPristine();
          this.savedAt.set(new Date());
        },
      });
      return;
    }

    this.directoryStore.createCompanyMutation.mutate(this.mapToCreateRequest(value), {
      onSuccess: () => {
        // Pristine first, otherwise the unsaved-changes guard blocks the redirect
        this.form.markAsPristine();
        this.router.navigate(['/' + RouteUri.DIRECTORY, RouteUri.DIRECTORY_COMPANIES]);
      },
    });
  }

  // « Ignorer » : revient aux valeurs du serveur en édition, à un formulaire vierge en création
  protected discard() {
    const company = this.companyQuery.data()?.company;
    if (company) {
      this.patchFromCompany(company);
    } else {
      this.form.reset();
      this.applyCreateDefaults();
    }

    this.form.markAsPristine();
  }

  //#region Mapper

  private mapToCreateRequest(company: CreateCompanyFormValue): CreateCompanyRequest {
    return {
      name: company.name ?? '',
      vatNumber: company.vatNumber ?? '',
      webSite: company.website ?? '',
      email: company.email ?? '',
      isActive: company.isActive ?? true,
      accountingAccountName: company.accountingAccountName,
      isSameAddressForBillingAddress: company.isSameAddressForBilling ?? true,
      address: company.addressForm ?? null,
      billingAddress: company.billingAddressForm ?? null,
      note: company.note ?? '',
      contactIds: company.contactIds ?? [],
      legalEntityAssignedId: company.legalEntityAssignedId,
      localeIsoCode: company.localeIsoCode,
      paymentModeId: company.paymentModeId,
      directDebit: this.isDirectDebit() ? this.mapToDirectDebitMandate(company.directDebitForm) : undefined
    }
  }

  private mapToUpdateRequest(value: CreateCompanyFormValue, company: CompanyDetails): UpdateCompanyRequest {
    return {
      name: value.name ?? '',
      vatNumber: value.vatNumber ?? '',
      webSite: value.website ?? '',
      email: value.email ?? '',
      isActive: value.isActive,
      crmId: company.crmId,
      accountingAccountName: value.accountingAccountName,
      isSameAddressForBillingAddress: value.isSameAddressForBilling ?? true,
      address: value.addressForm,
      billingAddress: value.billingAddressForm,
      note: value.note ?? '',
      contactIds: value.contactIds ?? [],
      legalEntityAssignedId: value.legalEntityAssignedId,
      localeIsoCode: value.localeIsoCode,
      paymentModeId: value.paymentModeId,
      directDebit: this.isDirectDebit() ? this.mapToDirectDebitMandate(value.directDebitForm) : undefined
    }
  }

  private mapToDirectDebitMandate(value: DirectDebitFormValue): DirectDebitMandate {
    return {
      iban: value.iban,
      bic: value.bic,
      reference: value.reference,
      signedAt: value.signedAt,
    };
  }

  //#endregion

  //#region Forms Factory

  private createCompanyForm(): FormGroup<CompanyFormModel> {
    const addressForm = this.createAddressForm();
    return new FormGroup<CompanyFormModel>({
      name: new FormControl(''),
      // Stable identifier: server-generated, read-only, never editable.
      guid: new FormControl<string | null>({value: null, disabled: true}),
      website: new FormControl(''),
      email: new FormControl(''),
      // The VAT is only format-checked when the company address country is Belgium
      vatNumber: new FormControl('', belgianVatValidator(() => addressForm.controls.countryIsoCode.value)),
      note: new FormControl('', {nonNullable: true}),
      contactIds: new FormControl<number[]>([], {nonNullable: true}),
      legalEntityAssignedId: new FormControl<number | null>(null),
      accountingAccountName: new FormControl<string | null>(null),
      localeIsoCode: new FormControl<string | null>(null),
      isSameAddressForBilling: new FormControl<boolean>(true, {nonNullable: true}),
      isActive: new FormControl<boolean>(true, {nonNullable: true}),
      addressForm,
      billingAddressForm: this.createAddressForm(),
      paymentModeId: new FormControl<number | null>(null),
      directDebitForm: this.createDirectDebitForm()
    });
  }

  private createDirectDebitForm(): FormGroup<DirectDebitFormModel> {
    return new FormGroup<DirectDebitFormModel>({
      iban: new FormControl<string | null>(null),
      bic: new FormControl<string | null>(null),
      reference: new FormControl<string | null>(null),
      signedAt: new FormControl<Date | null>(null)
    });
  }

  private createAddressForm(): FormGroup<AddressFormModel> {
    return new FormGroup<AddressFormModel>({
      id: new FormControl(0),
      street: new FormControl<string | null>(null),
      number: new FormControl<string | null>(null),
      postalCode: new FormControl<string | null>(null),
      city: new FormControl<string | null>(null),
      countryIsoCode: new FormControl('BE', {nonNullable: true, validators: Validators.required})
    });
  }

  //#endregion

}

// Validates the VAT number only for Belgian companies; empty is allowed (the field is optional)
function belgianVatValidator(getCountryIsoCode: () => string | null): ValidatorFn {
  return (control) => {
    if (getCountryIsoCode()?.toUpperCase() !== 'BE') return null;

    const vat = (control.value ?? '').trim();
    if (!vat) return null;

    return isBelgianVatValid(vat) ? null : {belgianVat: true};
  };
}

// Belgian VAT: BE prefix followed by 10 digits, where the last 2 are a mod 97 check on the first 8
function isBelgianVatValid(vat: string): boolean {
  if (!vat.toUpperCase().startsWith('BE')) return false;

  const digits = vat.slice(2);
  if (!/^\d{10}$/.test(digits)) return false;

  const base = Number(digits.slice(0, 8));
  const checkDigits = Number(digits.slice(-2));
  return 97 - (base % 97) === checkDigits;
}
